import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { ScreenCanvas } from "../components/configurator/ScreenCanvas";
import { EdgeConfigPanel } from "../components/configurator/EdgeConfigPanel";
import { RefreshCw, Layout, Link as LinkIcon, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import "../styles/configurator.css";

/**
 * ConfiguratorErrorBoundary Component
 * 
 * Intercepts any rendering errors or unexpected exceptions in the configurator tree
 * (such as null screen references, invalid dimensions, etc.) and presents a clean,
 * styled fallback UI rather than crashing the entire react application.
 */
class ConfiguratorErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ConfiguratorErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="configurator-error"
          style={{
            maxWidth: "600px",
            margin: "4rem auto",
            padding: "2.5rem 2rem",
            backgroundColor: "var(--color-bg-secondary)",
            border: "1px solid var(--color-error)",
            borderRadius: "var(--border-radius-lg)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1.25rem",
            boxShadow: "0 4px 20px rgba(239, 68, 68, 0.1)"
          }}
        >
          <AlertCircle size={48} color="var(--color-error)" />
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Unable to Load Configurator</h2>
          <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem", lineHeight: "1.5" }}>
            {this.state.error?.message || "An unexpected error occurred while communicating with the database."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: "var(--color-bg-primary)",
              color: "var(--color-text-primary)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--border-radius-md)",
              padding: "0.65rem 1.5rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "border-color 0.2s"
            }}
          >
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * ConfiguratorPage Shell
 * 
 * Manages fetching of layout and computer assets. It coordinates dynamic layout seeding
 * and triggers loading, empty (no paired devices), and success (configured editor) states.
 * 
 * Non-coder guide:
 * - Stagger X spacing: we use 2000 pixels (index * 2000) so multi-monitor setups don't overlap in the canvas grid.
 * - Width / Height: default virtual monitor resolutions are initialized to standard Full HD (1920x1080).
 */
export const ConfiguratorPage: React.FC = () => {
  // Query layout and computers reactively from Convex
  const layout = useQuery(api.layouts.getUserLayout);
  const computers = useQuery(api.computers.getUserComputers);

  // Setup mutations to create/activate default layout if none exists
  const saveLayout = useMutation(api.layouts.saveLayout);
  const setActiveLayout = useMutation(api.layouts.setActiveLayout);

  // Tracks if layout seeding has already started to prevent duplicate database writes
  const isSeeding = useRef(false);
  const [seedingError, setSeedingError] = useState<string | null>(null);

  // We determine if layout is missing (null) or empty (has 0 screens configured).
  // This solves seeding deadlocks where a layout entry exists in database but contains no monitors.
  // Verbose comment for non-coders: layout.screens holds the physical placements of monitors. If it's 0, it's essentially blank.
  const isLayoutMissingOrEmpty = layout === null || (layout !== undefined && layout !== null && layout.screens.length === 0);

  // Effect to automatically generate a layout if one doesn't exist but computers are paired
  useEffect(() => {
    // Trigger seeding if layout is missing/empty, we have connected computers, and we are not currently seeding.
    if (isLayoutMissingOrEmpty && computers !== undefined && computers.length > 0 && !isSeeding.current) {
      isSeeding.current = true;
      
      const seedLayout = async () => {
        try {
          // Construct screens with standard dimensions and staggered placement
          const screens = computers.map((comp, index) => ({
            computerId: comp._id,
            monitorId: "primary",
            width: 1920,
            height: 1080,
            x: index * 2000, // staggered coordinate offsets to prevent overlap on grid canvas
            y: 0,
          }));

          // Insert layout configuration
          const layoutId = await saveLayout({
            name: "Default Arrangement",
            screens,
            edgeConfig: {
              mode: "instant",
            },
          });

          // Set the seeded configuration as the active layout
          await setActiveLayout({ layoutId });
        } catch (err: unknown) {
          console.error("Failed to seed default layout:", err);
          const errorMessage = err instanceof Error ? err.message : "An error occurred during layout seeding.";
          setSeedingError(errorMessage);
          isSeeding.current = false;
        }
      };

      seedLayout();
    }
  }, [isLayoutMissingOrEmpty, layout, computers, saveLayout, setActiveLayout]);

  // State 1: Error during seeding or query failures
  if (seedingError) {
    return (
      <div
        data-testid="configurator-error"
        style={{
          maxWidth: "600px",
          margin: "4rem auto",
          padding: "2.5rem 2rem",
          backgroundColor: "var(--color-bg-secondary)",
          border: "1px solid var(--color-error)",
          borderRadius: "var(--border-radius-lg)",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1.25rem",
          boxShadow: "0 4px 20px rgba(239, 68, 68, 0.1)"
        }}
      >
        <AlertCircle size={48} color="var(--color-error)" />
        <h2 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Initialization Failed</h2>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "0.95rem" }}>
          {seedingError}
        </p>
        <button
          onClick={() => {
            setSeedingError(null);
            isSeeding.current = false;
            window.location.reload();
          }}
          style={{
            backgroundColor: "var(--color-bg-primary)",
            color: "var(--color-text-primary)",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--border-radius-md)",
            padding: "0.65rem 1.5rem",
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          Retry Seeding
        </button>
      </div>
    );
  }

  // State 2: Loading state (queries still loading or default layout is seeding)
  if (
    layout === undefined || 
    computers === undefined || 
    (isLayoutMissingOrEmpty && computers.length > 0)
  ) {
    return (
      <div
        data-testid="configurator-loading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: "1rem",
          color: "var(--color-text-secondary)"
        }}
      >
        <RefreshCw className="animate-spin" size={48} color="var(--color-accent)" />
        <p style={{ fontSize: "1.1rem", fontWeight: 500 }}>
          {layout === null ? "Initializing default screen arrangement..." : "Loading configurator workspace..."}
        </p>
      </div>
    );
  }

  // State 3: Empty state (no computers are registered to the user account)
  if (computers.length === 0) {
    return (
      <div
        data-testid="configurator-empty"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "4rem 2rem",
          minHeight: "60vh",
          textAlign: "center",
          gap: "1.5rem",
          maxWidth: "600px",
          margin: "0 auto"
        }}
      >
        <Layout size={64} color="var(--color-text-muted)" />
        <h2 style={{ fontSize: "1.75rem", fontWeight: 700, margin: 0 }}>No Computers Paired</h2>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "1rem", lineHeight: "1.6", margin: 0 }}>
          To set up your MultiKeys keyboard/mouse sharing layout, you must pair at least one computer.
          Download the client on your devices and register them using your license key.
        </p>
        <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
          <Link
            to="/dashboard"
            data-testid="go-to-dashboard-btn"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              backgroundColor: "var(--color-accent)",
              color: "var(--color-bg-primary)",
              textDecoration: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "var(--border-radius-md)",
              fontWeight: 700,
              fontSize: "0.95rem",
              transition: "opacity 0.2s"
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = "0.9"}
            onMouseOut={(e) => e.currentTarget.style.opacity = "1"}
          >
            <LinkIcon size={18} />
            Pair Devices on Dashboard
          </Link>
          <Link
            to="/download"
            style={{
              display: "inline-flex",
              alignItems: "center",
              backgroundColor: "var(--color-bg-secondary)",
              color: "var(--color-text-primary)",
              textDecoration: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "var(--border-radius-md)",
              fontWeight: 600,
              border: "1px solid var(--color-border)",
              fontSize: "0.95rem",
              transition: "border-color 0.2s"
            }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = "var(--color-accent)"}
            onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--color-border)"}
          >
            Download Client
          </Link>
        </div>
      </div>
    );
  }

  // State 4: Success state (data fully loaded and valid layout available)
  if (!layout) {
    return null;
  }

  return (
    <div 
      data-testid="configurator-page"
      style={{
        padding: "2rem",
        maxWidth: "1400px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "2rem"
      }}
    >
      <div>
        <h1 style={{ fontSize: "2rem", fontWeight: 800, color: "var(--color-text-primary)", margin: 0 }}>
          Monitor Layout Configurator
        </h1>
        <p style={{ color: "var(--color-text-secondary)", fontSize: "1rem", marginTop: "0.5rem" }}>
          Drag and arrange screens side-by-side to configure the physical boundaries for keyboard and mouse transit.
        </p>
      </div>

      <ConfiguratorErrorBoundary>
        <div 
          className="configurator-workspace"
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: "2rem",
            alignItems: "flex-start"
          }}
        >
          {/* Main drag and drop arrangement area */}
          <div style={{ flex: "2 1 600px", minWidth: "300px" }}>
            <ScreenCanvas />
          </div>

          {/* Configuration panel settings sideblock */}
          <div style={{ flex: "1 1 300px", minWidth: "250px" }}>
            <EdgeConfigPanel 
              layoutId={layout._id} 
              edgeConfig={layout.edgeConfig} 
            />
          </div>
        </div>
      </ConfiguratorErrorBoundary>
    </div>
  );
};

export default ConfiguratorPage;
